/**
 * Thin fetch wrapper. Same-origin via Vite proxy in dev; same-origin via
 * FastAPI's static mount in production. Cookies are HttpOnly + SameSite=Strict
 * and sent automatically because of `credentials: "include"`.
 */

function extractMessage(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === "object" &&
    "detail" in payload &&
    typeof (payload as { detail: unknown }).detail === "string"
  ) {
    return (payload as { detail: string }).detail;
  }
  return null;
}

export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(status: number, payload: unknown) {
    super(extractMessage(payload) ?? `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;

function detectApiBase() {
  const configured = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
  if (configured) return configured;

  if (typeof window !== "undefined" && window.location.pathname.startsWith("/ngon/")) {
    return "/ngon";
  }

  return "";
}

const apiBase = detectApiBase();

export function apiPath(path: string) {
  return apiBase + path;
}

export function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === "") continue;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    return await res.text();
  } catch {
    return null;
  }
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: { Accept: "application/json" },
  };
  if (body !== undefined && body !== null) {
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(apiPath(path), init);
  } catch {
    throw new ApiError(0, { detail: "Network error" });
  }
  const payload = await readBody(res);
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload as T;
}

export async function requestMultipart<T>(
  path: string,
  formData: FormData,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiPath(path), {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" }, // do NOT set Content-Type — browser sets multipart boundary
      body: formData,
    });
  } catch {
    throw new ApiError(0, { detail: "Network error" });
  }
  const payload = await readBody(res);
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload as T;
}
